import React, { useState } from 'react';
import {
    Alert,
    Avatar,
    Button,
    Box,
    Chip,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import ClassIcon from '@mui/icons-material/Class';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TableChartIcon from '@mui/icons-material/TableChart';
import PostAddIcon from '@mui/icons-material/PostAdd';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ForumIcon from '@mui/icons-material/Forum';
import {
    BAND_COLOR,
    BAND_THRESHOLDS,
    BandBar,
    DashboardError,
    DashboardHeader,
    DashboardLoading,
    EmptyNote,
    Panel,
    PendingWidgets,
    QuickAction,
    QuickActions,
    SectionTable,
    StatCard,
    TeacherData,
    WidgetRow,
    BandKey,
    bandOf,
    findWidget,
    initialsOf,
    nameByUserId,
    useSchoolDashboard,
    widgetRows,
} from './schoolDashboard';
import { LatestTestPanel } from './latestTest';
import { BandStudentsDrawer, SectionBandBars, bandEntries, entryCounts } from './riskBands';

/**
 * MY CLASS -- the teacher's dashboard.
 *
 * Scoped to teacher_assignment, and that scoping happens server-side. Every
 * section and every student below was already filtered by the API against this
 * teacher's assignments before it was sent; nothing here narrows a wider list.
 * That matters: a filter applied in the browser is a display convenience, not
 * an authorisation boundary, and a teacher must not receive another section's
 * roster at all, let alone receive it and be trusted to hide it.
 *
 * The measured tiles come from the response's `widgets` array, which the API
 * has always sent and this screen previously ignored. They are rendered here
 * only where the row can be named from the roster on the same response --
 * `my_students` is keyed on `user_id`, which the student list resolves. The
 * subject and topic tiles are keyed on ids with no names anywhere in the
 * payload, so they stay out rather than showing a teacher "Topic 4471".
 */

const MyAssignments = ({ assignments }: { assignments: TeacherData['assignments'] }) => {
    const theme = useTheme();

    return (
        <Panel title="What I teach" icon={<MenuBookIcon fontSize="small" />} tone="secondary">
            {assignments.length === 0 ? (
                <EmptyNote>No assignments yet.</EmptyNote>
            ) : (
                <Stack spacing={1}>
                    {assignments.map(a => (
                        <Stack
                            key={a.id}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip size="small" label={a.section_label} sx={{ fontWeight: 700 }} />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {a.subject}
                                </Typography>
                            </Stack>
                            {a.is_class_teacher && (
                                <Tooltip title="You own this section">
                                    <StarBorderIcon fontSize="small" color="primary" />
                                </Tooltip>
                            )}
                        </Stack>
                    ))}
                </Stack>
            )}
        </Panel>
    );
};

/**
 * The bottom band of `my_students`, named.
 *
 * One list, read two ways -- the same rows the band bar counts. A row is shown
 * with the reason it was flagged, because "this child is struggling" and "this
 * child has barely attempted anything" call for two different conversations.
 */
const NeedsAttention = ({
    rows,
    students,
    onSeeAll,
}: {
    rows: WidgetRow[];
    students: TeacherData['students'];
    onSeeAll?: () => void;
}) => {
    const theme = useTheme();
    const names = nameByUserId(students);

    // The chip counts everyone in the band; only the worst eight are listed.
    const all = rows
        .filter(r => bandOf(r) === 'intervention')
        .sort((a, b) => Number(a.accuracy_pct ?? 0) - Number(b.accuracy_pct ?? 0));
    const flagged = all.slice(0, 8);

    return (
        <Panel
            title="Needs your attention"
            tone="error"
            icon={<PriorityHighIcon fontSize="small" />}
            subtitle={
                all.length > flagged.length
                    ? `Worst ${flagged.length} of ${all.length} below ${BAND_THRESHOLDS.watch}% accuracy, last 30 days`
                    : `Below ${BAND_THRESHOLDS.watch}% accuracy on assigned work, last 30 days`
            }
            action={
                all.length > 0 ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" color="error" label={all.length} sx={{ fontWeight: 700 }} />
                        {onSeeAll && (
                            <Button size="small" onClick={onSeeAll}>
                                See all
                            </Button>
                        )}
                    </Stack>
                ) : undefined
            }
        >
            {rows.length === 0 ? (
                <EmptyNote>
                    No attempt data for your sections yet, so there is nobody to flag.
                </EmptyNote>
            ) : flagged.length === 0 ? (
                <EmptyNote>
                    Nobody is below the intervention line right now. Worth a look at the
                    watch band before it slips.
                </EmptyNote>
            ) : (
                <Stack spacing={1}>
                    {flagged.map(r => {
                        const accuracy = Number(r.accuracy_pct ?? 0);
                        const missed = Number(r.assignments_missed ?? 0);
                        return (
                            <Stack
                                key={String(r.user_id)}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                    px: 1.25,
                                    py: 1,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.error.main, 0.06),
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        fontSize: 13,
                                        fontWeight: 700,
                                        bgcolor: alpha(theme.palette.error.main, 0.18),
                                        color: theme.palette.error.main,
                                    }}
                                >
                                    {initialsOf(names.get(Number(r.user_id)) || null)}
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                        {names.get(Number(r.user_id)) || `Student #${r.user_id}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {Number(r.questions_attempted ?? 0)} answered
                                        {missed > 0 ? ` · ${missed} missed` : ''}
                                    </Typography>
                                </Box>
                                <Chip
                                    size="small"
                                    label={`${accuracy}%`}
                                    color="error"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Stack>
                        );
                    })}
                </Stack>
            )}
        </Panel>
    );
};

/**
 * The roster, with each student's band where one has been measured.
 *
 * A student with no metric row is shown plainly rather than being dropped: the
 * roster is the roster, and a child who has attempted nothing is exactly who a
 * teacher is looking for on this screen.
 */
const MyStudents = ({
    students,
    rows,
}: {
    students: TeacherData['students'];
    rows: WidgetRow[];
}) => {
    const theme = useTheme();
    const byUser = new Map<number, WidgetRow>();
    rows.forEach(r => byUser.set(Number(r.user_id), r));

    return (
        <Panel
            title="My students"
            icon={<TableChartIcon fontSize="small" />}
            action={<Chip size="small" label={`${students.length} enrolled`} />}
        >
            {students.length === 0 ? (
                <EmptyNote>No students are enrolled in your sections yet.</EmptyNote>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Roll</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: 180 }}>Accuracy</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {students.map(s => {
                            const row = byUser.get(Number(s.user_id));
                            const band = row ? bandOf(row) : 'nodata';
                            const accuracy = row?.accuracy_pct;
                            const tone = theme.palette[BAND_COLOR[band]].main;

                            return (
                                <TableRow key={s.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                    <TableCell>{s.roll_number || '—'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    bgcolor: alpha(tone, 0.15),
                                                    color: tone,
                                                }}
                                            >
                                                {initialsOf(s.full_name)}
                                            </Avatar>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {s.full_name}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" variant="outlined" label={s.section_label} />
                                    </TableCell>
                                    <TableCell>
                                        {accuracy === null || accuracy === undefined ? (
                                            <Typography variant="caption" color="text.secondary">
                                                Not enough data
                                            </Typography>
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 700, minWidth: 38, color: tone }}
                                                >
                                                    {Number(accuracy)}%
                                                </Typography>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(100, Number(accuracy))}
                                                    sx={{
                                                        flex: 1,
                                                        height: 6,
                                                        borderRadius: 3,
                                                        bgcolor: alpha(tone, 0.14),
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: tone,
                                                            borderRadius: 3,
                                                        },
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Panel>
    );
};

const ATTENTION_ANCHOR = 'teacher-needs-attention';

/**
 * The four actions the mock-up puts at the top of MY CLASS (requirement T-7).
 *
 * Three of them have a screen behind them today and go straight to it. The
 * fourth does not: there is no teacher-to-class messaging path anywhere in the
 * platform, so it is shown disabled with that reason rather than linked to a
 * route that would 404. `hasSections` disables the two authoring actions for a
 * teacher who has not been given a section yet -- there is nobody to set work
 * for, and finding that out on a half-filled form is worse than being told
 * here.
 */
const teacherActions = (hasSections: boolean): QuickAction[] => [
    {
        label: 'Set an assignment',
        hint: 'Give work to one of my sections',
        icon: <PostAddIcon fontSize="small" />,
        tone: 'primary',
        to: '/assignment/create',
        unavailable: hasSections ? undefined : 'No section assigned to you yet',
    },
    {
        label: 'Create a test',
        hint: 'Build a test from the question bank',
        icon: <FactCheckIcon fontSize="small" />,
        tone: 'secondary',
        to: '/school-test/create',
        unavailable: hasSections ? undefined : 'No section assigned to you yet',
    },
    {
        label: 'Weak students',
        hint: 'Jump to who needs attention',
        icon: <PriorityHighIcon fontSize="small" />,
        tone: 'error',
        onClick: () =>
            document
                .getElementById(ATTENTION_ANCHOR)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    },
    {
        label: 'Message class',
        hint: 'Send a note to the whole section',
        icon: <ForumIcon fontSize="small" />,
        tone: 'info',
        unavailable: 'Class messaging is not built yet',
    },
];

const SECTIONS_ANCHOR = 'teacher-sections';
const ASSIGNMENTS_ANCHOR = 'teacher-assignments';

const TeacherDashboard = () => {
    const { data, loading, error } = useSchoolDashboard<TeacherData>('teacher');
    const [drill, setDrill] = useState<{ open: boolean; band: BandKey | 'all'; section: string }>({
        open: false,
        band: 'intervention',
        section: '',
    });

    if (loading) return <DashboardLoading />;
    if (error) return <DashboardError message={error} />;
    if (!data) return <DashboardError message="No dashboard data was returned." />;

    const totalStudents = data.students.length;
    const classTeacherOf = data.assignments.filter(a => a.is_class_teacher).length;

    // `performance_bands` and `students_needing_attention` are the same
    // `my_students` query read two ways, exactly as the API describes them.
    const bandWidget = findWidget(data.widgets, 'performance_bands');
    const studentRows = widgetRows(data.widgets, 'performance_bands');
    // Banded over this teacher's roster, as on the principal screen, so the
    // counts match the list the drawer opens.
    const entries = bandEntries(studentRows, data.students);
    const counts = entryCounts(entries);
    const measured = counts.healthy + counts.watch + counts.intervention;
    const openBand = (band: BandKey | 'all', section = '') => setDrill({ open: true, band, section });
    const scrollTo = (id: string) =>
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    return (
        <Box sx={{ mt: 1 }}>
            <DashboardHeader
                subtitle="My Class"
                title={data.teacher.full_name || 'My class'}
                school={data.school}
                year={data.academic_year}
                icon={<ClassIcon />}
            />

            {/* Master says they are a teacher here; the school's own database has
                no record of them. Surfaced rather than hidden -- it means
                onboarding stopped halfway and the school admin has to finish it. */}
            {data.teacher.record_missing && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Your teacher record has not been created in this school yet. Ask your
                    school administrator to add you to the staff roster.
                </Alert>
            )}

            {data.sections.length === 0 && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    You have not been assigned to any class section yet, so there is
                    nothing to show. Your school administrator assigns sections.
                </Alert>
            )}

            <QuickActions actions={teacherActions(data.sections.length > 0)} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                <StatCard
                    label="My sections"
                    value={data.sections.length}
                    hint="Assigned to me"
                    icon={<ClassIcon fontSize="small" />}
                    tone="primary"
                    onClick={data.sections.length === 0 ? undefined : () => scrollTo(SECTIONS_ANCHOR)}
                    actionLabel="See sections"
                />
                <StatCard
                    label="My students"
                    value={totalStudents}
                    hint="Across my sections"
                    icon={<GroupsIcon fontSize="small" />}
                    tone="info"
                    onClick={totalStudents === 0 ? undefined : () => openBand('all')}
                    actionLabel="See all students"
                />
                <StatCard
                    label="Class teacher of"
                    value={classTeacherOf}
                    hint={classTeacherOf === 0 ? 'Subject teaching only' : 'Sections I own'}
                    icon={<StarBorderIcon fontSize="small" />}
                    tone="secondary"
                    onClick={data.assignments.length === 0 ? undefined : () => scrollTo(ASSIGNMENTS_ANCHOR)}
                    actionLabel="See my assignments"
                />
                <StatCard
                    label="Needs intervention"
                    value={measured === 0 ? '—' : counts.intervention}
                    hint={
                        measured === 0
                            ? 'No attempt data yet'
                            : `of ${measured} students with enough data`
                    }
                    icon={<PriorityHighIcon fontSize="small" />}
                    tone="error"
                    onClick={measured === 0 ? undefined : () => openBand('intervention')}
                    actionLabel="See who"
                    progress={measured === 0 ? null : (counts.intervention / measured) * 100}
                />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'stretch' }}>
                <Box sx={{ flex: '2 1 420px', minWidth: 320 }}>
                    <Panel
                        title="How my class is doing"
                        tone="success"
                        icon={<DonutLargeIcon fontSize="small" />}
                        subtitle={
                            bandWidget?.available
                                ? 'Accuracy on assigned work, last 30 days. Click a number, a bar or a section to see the students.'
                                : bandWidget?.reason ||
                                  'Assigned work only, last 30 days.'
                        }
                    >
                        <BandBar counts={counts} onSelect={band => openBand(band)} />
                        <SectionBandBars entries={entries} onSelect={openBand} />
                    </Panel>
                </Box>
                <Box id={ATTENTION_ANCHOR} sx={{ flex: '1 1 320px', minWidth: 300, scrollMarginTop: 16 }}>
                    <NeedsAttention
                        rows={studentRows}
                        students={data.students}
                        onSeeAll={() => openBand('intervention')}
                    />
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'stretch' }}>
                <Box id={SECTIONS_ANCHOR} sx={{ flex: '2 1 420px', minWidth: 320, scrollMarginTop: 16 }}>
                    <Panel title="My sections" icon={<ClassIcon fontSize="small" />}>
                        <SectionTable
                            sections={data.sections}
                            emptyMessage="No sections assigned."
                        />
                    </Panel>
                </Box>
                <Box id={ASSIGNMENTS_ANCHOR} sx={{ flex: '1 1 300px', minWidth: 280, scrollMarginTop: 16 }}>
                    <MyAssignments assignments={data.assignments} />
                </Box>
            </Box>

            <Box sx={{ mb: 2.5 }}>
                <LatestTestPanel test={data.latest_test} recent={data.recent_tests} />
            </Box>

            <Box sx={{ mb: 2.5 }}>
                <MyStudents students={data.students} rows={studentRows} />
            </Box>

            <PendingWidgets pending={data.pending} />

            <BandStudentsDrawer
                open={drill.open}
                onClose={() => setDrill(d => ({ ...d, open: false }))}
                entries={entries}
                initialBand={drill.band}
                initialSection={drill.section}
            />
        </Box>
    );
};

export default TeacherDashboard;
